<script setup>
import { computed, ref, watch } from 'vue'
import { useForm, Link, router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import StickyActionBar from '@/Components/UI/StickyActionBar.vue'
import Icon from '@/Components/UI/Icon.vue'
import CustomerChannels from '@/Components/CustomerChannels.vue'
import PhoneInput from '@/Components/UI/PhoneInput.vue'
import { DEFAULT_PLATFORMS } from '@/composables/useChannelConfig.js'
import { makesFor, modelsFor, extractCC } from '@/composables/useVehicleCatalog.js'
import { useCurrency } from '@/composables/useCurrency.js'
import BulletListInput from '@/Components/UI/BulletListInput.vue'

defineOptions({ layout: AppLayout })

const { t } = useI18n()
const { setting } = useCurrency()

const props = defineProps({
  vehicle:         { type: Object, required: true },
  errors:          { type: Object, default: () => ({}) },
  vehicle_classes: { type: Array,  default: () => [] },
  categories:      { type: Object, default: () => ({}) },
  transmissions:   { type: Array,  default: () => [] },
  fuel_types:      { type: Array,  default: () => [] },
  statuses:        { type: Array,  default: () => [] },
  suppliers:       { type: Array,  default: () => [] },
  rate_units:      { type: Array,  default: () => [] },
  initial_supplier_id: { type: Number, default: null },
  // Display-currency overrides — controller passes the enabled
  // non-canonical currencies + live FX rates so the form can show
  // auto-converted previews next to the "Set own price" checkbox.
  display_currencies: { type: Array,  default: () => [] },
  canonical_currency: { type: String, default: 'THB' },
  fx_margin_pct:      { type: Number, default: 1.0 },
  fx_rates:           { type: Object, default: () => ({}) }
})

const isEdit = !!props.vehicle.id

// ── Inline supplier creation (vehicle form keeps its state) ──
// The local supplier list starts from the controller-supplied prop but the
// inline-create flow can append to it via JSON POST so the operator never
// leaves the vehicle form.
const localSuppliers = ref([ ...props.suppliers ])
const newSupplierOpen   = ref(false)
const newSupplierSaving = ref(false)
const newSupplierError  = ref(null)
const seedSupplierChannels = () =>
  DEFAULT_PLATFORMS.map((platform, i) => ({ platform, handle: '', custom_label: '', position: i }))

const newSupplier = ref({
  name: '', contact_phone: '', contact_email: '', notes: '',
  contact_channels_attributes: seedSupplierChannels(),
})

const openNewSupplier = () => {
  newSupplier.value = {
    name: '', contact_phone: '', contact_email: '', notes: '',
    contact_channels_attributes: seedSupplierChannels(),
  }
  newSupplierError.value = null
  newSupplierOpen.value = true
}

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content || ''

const saveNewSupplier = async () => {
  if (newSupplierSaving.value) return
  if (!newSupplier.value.name.trim()) {
    newSupplierError.value = 'name_required'
    return
  }
  newSupplierSaving.value = true
  newSupplierError.value = null
  try {
    // Filter blank channels + reindex position, same shape as VehicleSources/Form.vue
    const cleanedChannels = (newSupplier.value.contact_channels_attributes || [])
      .filter(c => c.handle !== '' || c._destroy)
      .map((c, i) => ({ ...c, position: i }))
    const payload = {
      vehicle_source: {
        name:                        newSupplier.value.name,
        contact_phone:               newSupplier.value.contact_phone,
        contact_email:               newSupplier.value.contact_email,
        notes:                       newSupplier.value.notes,
        contact_channels_attributes: cleanedChannels,
      },
    }
    const res = await fetch('/vehicle_sources', {
      method: 'POST',
      headers: {
        'Content-Type':    'application/json',
        'Accept':          'application/json',
        'X-CSRF-Token':    csrfToken(),
        'X-Requested-With':'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      newSupplierError.value = body.errors ? Object.values(body.errors).flat().join(', ') : `HTTP ${res.status}`
      return
    }
    const created = await res.json()
    localSuppliers.value = [ ...localSuppliers.value, created ].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    subRental.value.source_id = created.id
    newSupplierOpen.value = false
  } catch (e) {
    newSupplierError.value = e.message || 'network'
  } finally {
    newSupplierSaving.value = false
  }
}

const form = useForm({
  make:                 props.vehicle.make ?? '',
  model:                props.vehicle.model ?? '',
  year:                 props.vehicle.year ?? '',
  registration:         props.vehicle.registration ?? '',
  vehicle_class:        props.vehicle.vehicle_class ?? 'two_wheeler',
  category:             props.vehicle.category ?? 'motorbike',
  transmission:         props.vehicle.transmission ?? '',
  seats:                props.vehicle.seats ?? '',
  fuel_type:            props.vehicle.fuel_type ?? '',
  colour:               props.vehicle.colour ?? '',
  daily_rate_cents:     props.vehicle.daily_rate_cents ?? 0,
  deposit_amount_cents: props.vehicle.deposit_amount_cents ?? 0,
  status:               props.vehicle.status ?? 'available',
  engine_cc:            props.vehicle.engine_cc ?? '',
  horsepower:           props.vehicle.horsepower ?? '',
  odometer_km:          props.vehicle.odometer_km ?? 0,
  last_service_km:      props.vehicle.last_service_km ?? '',
  next_service_km:      props.vehicle.next_service_km ?? '',
  notes:                props.vehicle.notes ?? '',
  description:   props.vehicle.description   ?? '',
  highlights:    props.vehicle.highlights    ?? [],
  features:      props.vehicle.features      ?? [],
  inclusions:    props.vehicle.inclusions    ?? [],
  requirements:  props.vehicle.requirements  ?? [],
  good_to_know:  props.vehicle.good_to_know  ?? [],
  photos:               [],
})

const availableMakes  = computed(() => makesFor(form.vehicle_class))
const availableModels = computed(() => modelsFor(form.vehicle_class, form.make))

// Hybrid dropdown — pick from the curated list, or choose "Other…" to type
// a custom value. Two local refs back the UI; a watcher composes them into
// form.make / form.model so the server-side payload stays a plain string.
const initialMakes  = makesFor(form.vehicle_class)
const initialModels = modelsFor(form.vehicle_class, form.make)

const makeChoice  = ref(form.make  && initialMakes.includes(form.make)   ? form.make  : (form.make  ? '__other' : ''))
const customMake  = ref(makeChoice.value === '__other' ? form.make : '')
const modelChoice = ref(form.model && initialModels.includes(form.model) ? form.model : (form.model ? '__other' : ''))
const customModel = ref(modelChoice.value === '__other' ? form.model : '')

watch([ makeChoice, customMake ], () => {
  form.make = makeChoice.value === '__other' ? customMake.value : makeChoice.value
  // Reset model when make changes — its list of valid models just shifted.
  if (makeChoice.value !== '__other') {
    const nextModels = modelsFor(form.vehicle_class, makeChoice.value)
    if (modelChoice.value && modelChoice.value !== '__other' && !nextModels.includes(modelChoice.value)) {
      modelChoice.value = ''
      customModel.value = ''
    }
  }
})

watch([ modelChoice, customModel ], () => {
  form.model = modelChoice.value === '__other' ? customModel.value : modelChoice.value
})

// When model changes, try to extract CC from the name and pre-fill the
// engine_cc field. Overwrites unconditionally — the operator can still
// type anything afterwards.
watch(() => form.model, (newModel) => {
  if (!newModel) return
  const cc = extractCC(newModel)
  if (cc != null) form.engine_cc = cc
})

// When vehicle_class flips (two-wheeler ↔ four-wheeler), clear make + model
// so the operator picks fresh from the right catalog.
watch(() => form.vehicle_class, () => {
  makeChoice.value  = ''
  customMake.value  = ''
  modelChoice.value = ''
  customModel.value = ''
})

// ── Optional inline sub-rental (create flow only) ────────────────────
// When the operator marks the vehicle as sub-rented from a supplier,
// these fields are submitted alongside the vehicle and a VehicleSubRental
// row is created in the same transaction by VehiclesController#create.
// initial_supplier_id pre-selects the supplier and opens the sub-rental
// section when the operator landed here via the "Add new vehicle" CTA on a
// VehicleSource show page (which always means: a new bike from this supplier).
const subRentalEnabled = ref(!isEdit && !!props.initial_supplier_id)
const subRental = ref({
  source_id:      props.initial_supplier_id ? String(props.initial_supplier_id) : '',
  start_on:       new Date().toISOString().slice(0, 10),
  end_on:         '',
  rate_amount:    '',
  rate_unit:      'day',
  deposit_amount: ''
})


// ── Display-currency price overrides ────────────────────────────
// For each non-canonical display currency the operator has enabled,
// the form shows the auto-converted price plus a "Set own price"
// toggle that switches to manual entry. Stored values arrive in cents
// (from vehicle.vehicle_prices); we keep them in cents in the form
// state and convert to/from major units only at the input layer.
const FORMATS = {
  THB: { symbol: '฿', decimals: 2 },
  USD: { symbol: '$', decimals: 2 },
  RUB: { symbol: '₽', decimals: 2 },
  CNY: { symbol: '¥', decimals: 2 }
}

const overrideState = ref({})
props.display_currencies.forEach(code => {
  const existing = (props.vehicle.vehicle_prices || []).find(p => p.currency_code === code)
  overrideState.value[code] = {
    id:                   existing?.id || null,
    daily_rate_enabled:   existing && existing.daily_rate_cents != null,
    deposit_enabled:      existing && existing.deposit_amount_cents != null,
    daily_rate_cents:     existing?.daily_rate_cents ?? null,
    deposit_amount_cents: existing?.deposit_amount_cents ?? null,
    destroy:              false
  }
})

function autoConverted(field, code) {
  const rate = props.fx_rates[code]
  if (!rate) return null
  const canonicalCents = form[field] || 0
  const margin = 1 + (props.fx_margin_pct / 100.0)
  return Math.round(canonicalCents * rate * margin)
}

function formatCents(cents, code) {
  if (cents == null) return '—'
  const fmt = FORMATS[code] || { symbol: code, decimals: 2 }
  return `${fmt.symbol}${(cents / 100).toFixed(fmt.decimals)}`
}

function inputCurrencyToCents(input, code) {
  const num = parseFloat(input)
  if (Number.isNaN(num)) return null
  return Math.round(num * 100)
}

function inputCurrencyFromCents(cents) {
  if (cents == null) return ''
  return (cents / 100).toString()
}

const subtypeOptions = computed(() => props.categories[form.vehicle_class] ?? [])

watch(() => form.vehicle_class, (newClass, oldClass) => {
  if (oldClass === undefined) return
  const allowed = props.categories[newClass] ?? []
  if (!allowed.includes(form.category)) {
    form.category = allowed[0] ?? ''
  }
  // Clear four-wheeler-only fields when switching to two-wheeler so stale
  // transmission / seats / fuel_type don't get persisted on a bike record.
  if (newClass !== 'four_wheeler') {
    form.transmission = ''
    form.seats        = ''
    form.fuel_type    = ''
  }
})

function buildFormData() {
  const fd = new FormData()
  const fields = ['make', 'model', 'year', 'registration',
                  'vehicle_class', 'category', 'transmission', 'seats', 'fuel_type',
                  'colour', 'daily_rate_cents', 'deposit_amount_cents', 'status',
                  'engine_cc', 'horsepower',
                  'odometer_km', 'last_service_km', 'next_service_km', 'notes',
                  'description']
  fields.forEach(k => fd.append(`vehicle[${k}]`, form[k] ?? ''))
  const listFields = ['highlights', 'features', 'inclusions', 'requirements', 'good_to_know']
  listFields.forEach(k => {
    const arr = form[k] ?? []
    if (arr.length === 0) {
      fd.append(`vehicle[${k}][]`, '')
    } else {
      arr.forEach(v => fd.append(`vehicle[${k}][]`, v))
    }
  })
  form.photos.forEach(file => fd.append('vehicle[photos][]', file))

  // Per-currency override rows. Three states per (vehicle × currency):
  //   - both fields disabled: emit _destroy=1 if a row already exists
  //   - at least one field enabled: emit currency_code + filled fields
  //                                 (unfilled stays NULL on the DB row)
  //   - no row + no toggle: emit nothing
  props.display_currencies.forEach((code, idx) => {
    const s = overrideState.value[code]
    if (!s) return
    const hasAnyEnabled = s.daily_rate_enabled || s.deposit_enabled
    if (s.id && !hasAnyEnabled) {
      // Override row exists but operator unticked everything — destroy.
      fd.append(`vehicle[vehicle_prices_attributes][${idx}][id]`, s.id)
      fd.append(`vehicle[vehicle_prices_attributes][${idx}][_destroy]`, '1')
    } else if (hasAnyEnabled) {
      if (s.id) fd.append(`vehicle[vehicle_prices_attributes][${idx}][id]`, s.id)
      fd.append(`vehicle[vehicle_prices_attributes][${idx}][currency_code]`, code)
      fd.append(`vehicle[vehicle_prices_attributes][${idx}][daily_rate_cents]`,
                s.daily_rate_enabled && s.daily_rate_cents != null ? s.daily_rate_cents : '')
      fd.append(`vehicle[vehicle_prices_attributes][${idx}][deposit_amount_cents]`,
                s.deposit_enabled && s.deposit_amount_cents != null ? s.deposit_amount_cents : '')
    }
  })

  return fd
}

function submit() {
  const fd = buildFormData()
  const opts = {
    onError: (errors) => { form.errors = errors },
    preserveScroll: true,
  }
  if (isEdit) {
    fd.append('_method', 'PATCH')
    router.post(`/vehicles/${props.vehicle.id}`, fd, opts)
  } else {
    router.post('/vehicles', fd, opts)
  }
}

function onPhotos(e) {
  form.photos = Array.from(e.target.files)
}

function err(field) {
  return form.errors[field] || props.errors[field]?.[0]
}

// Format an integer cents value for an input, dropping trailing zeros
// when the value is a whole unit (so 30000 cents → "300", not "300.00").
function centsToInput(cents) {
  if (cents == null) return ''
  const v = cents / 100
  return Number.isInteger(v) ? String(v) : v.toFixed(2)
}

const dailyRateDollars = {
  get: () => centsToInput(form.daily_rate_cents),
  set: (v) => { form.daily_rate_cents = Math.round(parseFloat(v) * 100) || 0 },
}
const depositDollars = {
  get: () => centsToInput(form.deposit_amount_cents),
  set: (v) => { form.deposit_amount_cents = Math.round(parseFloat(v) * 100) || 0 },
}

const inputBase = 'w-full rounded-[var(--radius-md)] border bg-[color:var(--surface)] text-[color:var(--ink)] px-3 py-2 text-sm focus:outline-2 focus:outline-[color:var(--accent)]'
const labelCls  = 'block text-sm font-medium text-[color:var(--inkSoft)] mb-1'
const errorCls  = 'mt-1 text-xs text-[color:var(--danger)]'
</script>

<template>
  <PageShell :title="isEdit ? 'Edit Vehicle' : 'Add Vehicle'">
    <div class="max-w-2xl mx-auto w-full">
      <Link
        href="/vehicles"
        class="inline-flex items-center text-[color:var(--muted)] hover:text-[color:var(--ink)] transition-colors mb-4"
        aria-label="Back"
      >
        <Icon name="chev-l" :size="18" />
      </Link>

      <form
        id="vehicle-form"
        @submit.prevent="submit"
        class="rounded-[var(--radius-lg)] border border-[color:var(--line)] bg-[color:var(--surface)] p-6 space-y-5"
      >
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label :class="labelCls">Make *</label>
            <select
              v-model="makeChoice"
              required
              :class="[inputBase, err('make') ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]']"
            >
              <option value="">— Pick a make —</option>
              <option v-for="m in availableMakes" :key="m" :value="m">{{ m }}</option>
              <option value="__other">Other (type custom)…</option>
            </select>
            <input
              v-if="makeChoice === '__other'"
              v-model="customMake"
              type="text"
              required
              placeholder="Type make"
              :class="[inputBase, 'mt-2', err('make') ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]']"
            />
            <p v-if="err('make')" :class="errorCls">{{ err('make') }}</p>
          </div>
          <div>
            <label :class="labelCls">Model *</label>
            <select
              v-model="modelChoice"
              required
              :disabled="!form.make"
              :class="[inputBase, err('model') ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]', !form.make ? 'opacity-60' : '']"
            >
              <option value="">— Pick a model —</option>
              <option v-for="m in availableModels" :key="m" :value="m">{{ m }}</option>
              <option value="__other">Other (type custom)…</option>
            </select>
            <input
              v-if="modelChoice === '__other'"
              v-model="customModel"
              type="text"
              required
              placeholder="Type model"
              :class="[inputBase, 'mt-2', err('model') ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]']"
            />
            <p v-if="err('model')" :class="errorCls">{{ err('model') }}</p>
          </div>
        </div>

        <div class="grid grid-cols-3 gap-4">
          <div>
            <label :class="labelCls">Year</label>
            <input v-model="form.year" type="number" min="1900" :max="new Date().getFullYear() + 1" :class="[inputBase, 'border-[color:var(--line)]']" />
          </div>
          <div>
            <label :class="labelCls">Registration *</label>
            <input v-model="form.registration" type="text" required :class="[inputBase, 'font-mono uppercase', err('registration') ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]']" />
            <p v-if="err('registration')" :class="errorCls">{{ err('registration') }}</p>
          </div>
          <div>
            <label :class="labelCls">Colour</label>
            <input v-model="form.colour" type="text" :class="[inputBase, 'border-[color:var(--line)]']" />
          </div>
        </div>

        <div class="grid grid-cols-3 gap-4">
          <div>
            <label :class="labelCls">{{ t('vehicles.fields.class') }}</label>
            <select v-model="form.vehicle_class" :class="[inputBase, 'border-[color:var(--line)] capitalize']">
              <option v-for="c in vehicle_classes" :key="c" :value="c">
                {{ t(`vehicles.classes.${c}`) }}
              </option>
            </select>
          </div>
          <div>
            <label :class="labelCls">{{ t('vehicles.fields.subtype') }}</label>
            <select v-model="form.category" :class="[inputBase, 'border-[color:var(--line)] capitalize']">
              <option v-for="s in subtypeOptions" :key="s" :value="s">{{ s }}</option>
            </select>
            <p v-if="err('category')" :class="errorCls">{{ err('category') }}</p>
          </div>
          <div>
            <label :class="labelCls">Status</label>
            <select v-model="form.status" :class="[inputBase, 'border-[color:var(--line)] capitalize']">
              <option v-for="s in statuses" :key="s" :value="s">{{ s.charAt(0).toUpperCase() + s.slice(1) }}</option>
            </select>
          </div>
        </div>

        <div v-if="form.vehicle_class === 'four_wheeler'" class="grid grid-cols-3 gap-4">
          <div>
            <label :class="labelCls">{{ t('vehicles.fields.transmission') }} *</label>
            <select v-model="form.transmission" required :class="[inputBase, err('transmission') ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]', 'capitalize']">
              <option value="" disabled>—</option>
              <option v-for="tr in transmissions" :key="tr" :value="tr">{{ tr.replace('_', ' ') }}</option>
            </select>
            <p v-if="err('transmission')" :class="errorCls">{{ err('transmission') }}</p>
          </div>
          <div>
            <label :class="labelCls">{{ t('vehicles.fields.seats') }} *</label>
            <input v-model.number="form.seats" type="number" min="1" max="30" required :class="[inputBase, err('seats') ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]']" />
            <p v-if="err('seats')" :class="errorCls">{{ err('seats') }}</p>
          </div>
          <div>
            <label :class="labelCls">{{ t('vehicles.fields.fuel_type') }} *</label>
            <select v-model="form.fuel_type" required :class="[inputBase, err('fuel_type') ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]', 'capitalize']">
              <option value="" disabled>—</option>
              <option v-for="f in fuel_types" :key="f" :value="f">{{ f }}</option>
            </select>
            <p v-if="err('fuel_type')" :class="errorCls">{{ err('fuel_type') }}</p>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label :class="labelCls">{{ t('vehicles.fields.defaultRate') }} ({{ setting.currency_code }}) *</label>
            <input :value="dailyRateDollars.get()" @input="dailyRateDollars.set($event.target.value)" type="number" min="0" step="1" required :class="[inputBase, err('daily_rate_cents') ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]']" />
            <p v-if="err('daily_rate_cents')" :class="errorCls">{{ err('daily_rate_cents') }}</p>
            <p class="mt-1 text-xs text-[color:var(--muted)]">
              {{ t('vehicles.fields.defaultRateHint') }}
            </p>
          </div>
          <div>
            <label :class="labelCls">Deposit ({{ setting.currency_code }})</label>
            <input :value="depositDollars.get()" @input="depositDollars.set($event.target.value)" type="number" min="0" step="1" :class="[inputBase, 'border-[color:var(--line)]']" />
          </div>
        </div>

        <!-- Per-currency display-price overrides — only renders when
             operator has enabled non-canonical display currencies. Each
             row shows auto-converted preview + a "Set own price" toggle. -->
        <div v-if="display_currencies.length > 0" class="rounded-xl border border-[color:var(--line)] p-4 space-y-4">
          <div>
            <h3 class="text-sm font-semibold text-[color:var(--ink)] mb-1">{{ t('vehicles.fields.displayPriceOverrides') }}</h3>
            <p class="text-xs text-[color:var(--muted)]">{{ t('vehicles.fields.displayPriceOverridesHint') }}</p>
          </div>
          <div v-for="code in display_currencies" :key="code" class="space-y-2 pt-2 border-t border-[color:var(--line)] first:border-t-0 first:pt-0">
            <div class="text-sm font-semibold text-[color:var(--inkSoft)]">{{ code }}</div>
            <div class="grid grid-cols-2 gap-4">
              <!-- Daily rate column -->
              <div class="space-y-1">
                <label class="flex items-center gap-2 text-xs text-[color:var(--inkSoft)]">
                  <input
                    type="checkbox"
                    :checked="overrideState[code]?.daily_rate_enabled"
                    @change="overrideState[code].daily_rate_enabled = $event.target.checked; if (!$event.target.checked) overrideState[code].daily_rate_cents = null"
                  />
                  <span>{{ t('vehicles.fields.setOwnDailyRate') }}</span>
                </label>
                <div v-if="overrideState[code]?.daily_rate_enabled">
                  <input
                    type="number" min="0" step="1"
                    :value="inputCurrencyFromCents(overrideState[code].daily_rate_cents)"
                    @input="overrideState[code].daily_rate_cents = inputCurrencyToCents($event.target.value, code)"
                    :class="[inputBase, 'border-[color:var(--line)]']"
                    :placeholder="`${FORMATS[code]?.symbol || ''}…`"
                  />
                </div>
                <div v-else class="text-xs text-[color:var(--muted)] italic">
                  {{ t('vehicles.fields.autoConverted') }}: {{ formatCents(autoConverted('daily_rate_cents', code), code) }}
                </div>
              </div>
              <!-- Deposit column -->
              <div class="space-y-1">
                <label class="flex items-center gap-2 text-xs text-[color:var(--inkSoft)]">
                  <input
                    type="checkbox"
                    :checked="overrideState[code]?.deposit_enabled"
                    @change="overrideState[code].deposit_enabled = $event.target.checked; if (!$event.target.checked) overrideState[code].deposit_amount_cents = null"
                  />
                  <span>{{ t('vehicles.fields.setOwnDeposit') }}</span>
                </label>
                <div v-if="overrideState[code]?.deposit_enabled">
                  <input
                    type="number" min="0" step="1"
                    :value="inputCurrencyFromCents(overrideState[code].deposit_amount_cents)"
                    @input="overrideState[code].deposit_amount_cents = inputCurrencyToCents($event.target.value, code)"
                    :class="[inputBase, 'border-[color:var(--line)]']"
                    :placeholder="`${FORMATS[code]?.symbol || ''}…`"
                  />
                </div>
                <div v-else class="text-xs text-[color:var(--muted)] italic">
                  {{ t('vehicles.fields.autoConverted') }}: {{ formatCents(autoConverted('deposit_amount_cents', code), code) }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label :class="labelCls">Engine (cc)</label>
            <input v-model.number="form.engine_cc" type="number" min="0" :class="[inputBase, 'border-[color:var(--line)]']" />
          </div>
          <div>
            <label :class="labelCls">Horsepower (hp)</label>
            <input v-model.number="form.horsepower" type="number" min="0" :class="[inputBase, 'border-[color:var(--line)]']" />
          </div>
        </div>

        <div class="grid grid-cols-3 gap-4">
          <div>
            <label :class="labelCls">Odometer (km)</label>
            <input v-model="form.odometer_km" type="number" min="0" :class="[inputBase, 'border-[color:var(--line)]']" />
          </div>
          <div>
            <label :class="labelCls">Last service at (km)</label>
            <input v-model="form.last_service_km" type="number" min="0" :class="[inputBase, 'border-[color:var(--line)]']" />
          </div>
          <div>
            <label :class="labelCls">Next Service at (km)</label>
            <input v-model="form.next_service_km" type="number" min="0" :class="[inputBase, 'border-[color:var(--line)]']" />
          </div>
        </div>

        <div>
          <label :class="labelCls">Photos</label>
          <input
            type="file"
            accept="image/*"
            multiple
            @change="onPhotos"
            class="w-full text-sm text-[color:var(--inkSoft)] file:mr-3 file:py-1.5 file:px-3 file:rounded-[var(--radius-md)] file:border-0 file:text-sm file:font-medium file:bg-[color:var(--accentSoft)] file:text-[color:var(--accent)] hover:file:brightness-95 file:cursor-pointer"
          />
          <p class="mt-1 text-xs text-[color:var(--muted)]">Select multiple images. Existing photos are kept unless replaced.</p>
        </div>

        <div>
          <label :class="labelCls">Notes</label>
          <textarea v-model="form.notes" rows="3" :class="[inputBase, 'border-[color:var(--line)]']"></textarea>
        </div>

        <div>
          <label :class="labelCls">{{ t('vehicles.fields.description') }}</label>
          <textarea v-model="form.description" rows="4" :class="[inputBase, 'border-[color:var(--line)]']"></textarea>
        </div>

        <BulletListInput v-model="form.highlights"   :label="t('vehicles.fields.highlights')"   :placeholder="t('vehicles.fields.highlights_ph')" />
        <BulletListInput v-model="form.features"     :label="t('vehicles.fields.features')"     :placeholder="t('vehicles.fields.features_ph')" />
        <BulletListInput v-model="form.inclusions"   :label="t('vehicles.fields.inclusions')"   :placeholder="t('vehicles.fields.inclusions_ph')" />
        <BulletListInput v-model="form.requirements" :label="t('vehicles.fields.requirements')" :placeholder="t('vehicles.fields.requirements_ph')" />
        <BulletListInput v-model="form.good_to_know" :label="t('vehicles.fields.good_to_know')" :placeholder="t('vehicles.fields.good_to_know_ph')" />

        <!-- Sub-rented vehicles are now supplier-portal-managed only. The
             operator creates only owned vehicles here; sub-rented bikes
             come in through the supplier's portal and route through the
             approval queue at /admin/approvals. -->
        <p v-if="!isEdit" class="text-xs text-[color:var(--muted)] italic">
          {{ t('vehicles.form.subRented.operatorNotice') }}
        </p>
      </form>

      <StickyActionBar>
        <UiButton
          type="submit"
          form="vehicle-form"
          variant="primary"
          :loading="form.processing"
        >
          {{ form.processing ? 'Saving…' : (isEdit ? 'Update Vehicle' : 'Add Vehicle') }}
        </UiButton>
      </StickyActionBar>
    </div>

    <!-- Inline supplier creation modal — keeps the vehicle form state intact -->
    <div
      v-if="newSupplierOpen"
      class="fixed inset-0 z-50 flex md:items-center md:justify-center md:p-4 bg-black/60"
      role="dialog"
      aria-modal="true"
      @click.self="newSupplierOpen = false"
    >
      <div class="w-full h-full md:h-auto md:max-w-md md:rounded-[var(--radius-lg)] md:shadow-[var(--shadow-lg)] bg-[color:var(--surface)] flex flex-col md:max-h-[calc(100vh-2rem)]">
        <header class="flex items-center justify-between p-4 border-b border-[color:var(--line)] flex-shrink-0">
          <h3 class="font-display text-base text-[color:var(--ink)]">
            {{ t('vehicles.form.subRented.createSupplier') }}
          </h3>
          <button
            type="button"
            class="text-[color:var(--muted)] hover:text-[color:var(--ink)]"
            :aria-label="t('common.close')"
            @click="newSupplierOpen = false"
          >
            <Icon name="x" :size="20" />
          </button>
        </header>
        <form
          id="new-supplier-form"
          class="flex-1 overflow-y-auto p-4 space-y-4"
          @submit.prevent="saveNewSupplier"
        >
          <div>
            <label :class="labelCls">{{ t('suppliers.form.name') }} *</label>
            <input
              v-model="newSupplier.name"
              type="text"
              required
              :class="[inputBase, 'border-[color:var(--line)]']"
            >
          </div>
          <div>
            <label :class="labelCls">{{ t('suppliers.form.phone') }}</label>
            <PhoneInput v-model="newSupplier.contact_phone" />
          </div>
          <div>
            <label :class="labelCls">{{ t('suppliers.form.email') }}</label>
            <input
              v-model="newSupplier.contact_email"
              type="email"
              :class="[inputBase, 'border-[color:var(--line)]']"
            >
          </div>
          <div>
            <label :class="labelCls">{{ t('suppliers.form.notes') }}</label>
            <textarea
              v-model="newSupplier.notes"
              rows="2"
              :class="[inputBase, 'border-[color:var(--line)] resize-none']"
            />
          </div>
          <div class="pt-2">
            <label :class="labelCls">{{ t('suppliers.form.channels') }}</label>
            <CustomerChannels v-model="newSupplier.contact_channels_attributes" />
          </div>
          <p
            v-if="newSupplierError"
            class="text-xs text-[color:var(--danger)]"
          >{{ newSupplierError }}</p>
        </form>
        <footer class="flex items-center justify-end gap-2 p-4 border-t border-[color:var(--line)] flex-shrink-0">
          <UiButton variant="secondary" @click="newSupplierOpen = false">
            {{ t('common.cancel') }}
          </UiButton>
          <UiButton
            variant="primary"
            type="submit"
            form="new-supplier-form"
            :loading="newSupplierSaving"
            :disabled="!newSupplier.name || newSupplierSaving"
          >
            {{ t('common.save') }}
          </UiButton>
        </footer>
      </div>
    </div>
  </PageShell>
</template>
